/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SERVICE_ADAPTERS, BandwidthService, Duration } from "./bandwidthServiceAdapters";
import { 
    buildControls,
    getTimeRangeOptions,
    getIntervalOptions,
    toSeconds,
    IntervalLiteral,
    IntervalOption
} from "./timeControls";
import { CartesianGrid, Label, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartContainer } from "./ChartContainer";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import { ReactComponent as IconInfo } from "../../../../shared/assets/Icons/circle-info-solid.svg";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  normalizePortBandwidthForDisplay,
  normalizeDciBandwidthForDisplay,
  peakMbps,
} from "./bandwidthDisplay";
import BandwidthPeaks, { type ChartEndpoint, type PeakSeries } from "./BandwidthPeaks";
import {
  validateDateRange,
  computeRangeSeconds,
  pickIntervalForRange,
  getIntervalOptionsForRange,
  isIntervalSafe,
  calculatePoints,
  normalizeDateRange,
  dateRangeToISOUTC,
  TARGET_POINTS,
  HARD_MAX_POINTS,
  MAX_RANGE_DAYS,
  VIRTUAL_PORT_MAX_RANGE_DAYS,
  type IntervalOption as CustomIntervalOption,
  type ExtendedIntervalLiteral,
} from "./dateRangeUtils";
import { ChartRange, readChartRange, sameChartRange, writeChartRange } from "./chartRangeParams";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";

interface BandwidthItem { time: string; value: number | null; }
interface BandwidthUtilization {
  inbound: BandwidthItem[]; 
  outbound?: BandwidthItem[];
  timeRange?: string; 
  interval?: string; 
  unit: string;
}
interface PortPerformanceData {
  portId?: number; connectionId?: number;
  bandwidthUtilization?: BandwidthUtilization;
  // Datacenter exposes a shared unit across both ports at the root level
  bandwidthUnit?: string;
  // Datacenter structure
  portA?: {
    subId?: string;
    bandwidthUtilization?: BandwidthUtilization;
  };
  portB?: {
    subId?: string;
    bandwidthUtilization?: BandwidthUtilization;
  };
  // The B end of a DCI is not always a port
  endpointBType?: "sdwan_hub" | "fwaas" | string;
  endpointB?: { subscriptionId?: string; name?: string };
}
function firstNonNullIdx(arr: BandwidthItem[]): number {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i]?.value != null) return i;
  }
  return arr.length;
}

function lastNonNullIdx(arr: BandwidthItem[]): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i]?.value != null) return i;
  }
  return -1;
}

function processData(perf: PortPerformanceData | null, isDatacenter = false) {
  // Handle datacenter structure (has portA/portB)
  if (isDatacenter && perf?.portA && perf?.portB) {
    // Check if bandwidthUtilization exists for both ports
    if (!perf.portA.bandwidthUtilization || !perf.portB.bandwidthUtilization) {
      return [];
    }
    const portA = perf.portA.bandwidthUtilization;
    const portB = perf.portB.bandwidthUtilization;
    
    const inboundA = portA.inbound || [];
    const outboundA = portA.outbound || [];
    const inboundB = portB.inbound || [];
    const outboundB = portB.outbound || [];

    // Validate that we have data arrays with content
    if (inboundA.length === 0 && inboundB.length === 0) {
      return [];
    }
        
    // Use inboundA as the base array (assuming all arrays have the same timestamps)
    // If inboundA is empty, fall back to inboundB
    const baseArray = inboundA.length > 0 ? inboundA : inboundB;

    return baseArray.map((item: BandwidthItem, index: number) => {
    // return inboundA.map((item: BandwidthItem, index: number) => {
      const time = item?.time || "";
      const d = time ? new Date(time) : new Date();

      const formattedTime = d.toLocaleString("en-US", {
        timeZone: "America/Mexico_City",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      // This ensures datacenter charts show continuous lines even with missing data points
      return {
        time,
        formattedTime,
        inboundA: inboundA[index]?.value ?? 0,
        outboundA: outboundA[index]?.value ?? 0,
        inboundB: inboundB[index]?.value ?? 0,
        outboundB: outboundB[index]?.value ?? 0,
      };
    });
  }
  
  // Note: portA/portB structure is only for datacenter, standard services use bandwidthUtilization directly
  const bandwidthUtilization = perf?.bandwidthUtilization;
  
  if (!bandwidthUtilization) return [];

  const { inbound, outbound = [] } = bandwidthUtilization;
  
  // Handle cases where outbound might be missing or empty
  const hasOutbound = outbound && outbound.length > 0;
  
  // compute unified slice window so both series drop null-only edges
  const inboundStart = firstNonNullIdx(inbound);
  const inboundEnd = lastNonNullIdx(inbound);
  const outboundStart = hasOutbound ? firstNonNullIdx(outbound) : inboundStart;
  const outboundEnd = hasOutbound ? lastNonNullIdx(outbound) : inboundEnd;
  
  const start = Math.max(inboundStart, outboundStart);
  const end = Math.min(inboundEnd, outboundEnd);
  
  if (start > end || start >= inbound.length || (hasOutbound && start >= outbound.length)) {
    return []; // nothing to plot
  }

  const inboundSlice = inbound.slice(start, end + 1);
  const outboundSlice = hasOutbound ? outbound.slice(start, end + 1) : [];
  return inboundSlice.map((item, i) => {
    const ts = item?.time ?? outboundSlice[i]?.time ?? "";
    const d = ts ? new Date(ts) : new Date();

    const formattedTime = d.toLocaleString("en-US", {
      timeZone: "America/Mexico_City",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    // IMPORTANT: keep null as undefined so Recharts skips it (no 0 spike)
    const inboundVal  = item?.value ?? undefined;
    const outboundVal = outboundSlice[i]?.value ?? undefined;

    return {
      time: ts,
      inbound: inboundVal,
      outbound: outboundVal,
      formattedTime,
    };
  });
}

type PortType = "physical" | "virtual";

// One colour per DCI series, shared by its line and its legend toggle so the two
// cannot drift apart (CTX-779).
const DCI_SERIES_COLORS = {
  inboundA: "var(--color-primitive-brand-surf-500)",
  outboundA: "var(--color-primitive-brand-surf-700)",
  inboundB: "var(--color-primitive-brand-fuchsia-200)",
  outboundB: "var(--color-primitive-brand-fuchsia-800)",
} as const;

// The B end of a DCI may be a port, an SD-WAN hub or a FWAAS. Each end is named
// by what it is — never by its id, which used to put "Port A" next to a bare
// subscription id in the legend — and the id rides along as a hover reference.
export function dciEndpoints(perf: PortPerformanceData | null): { a: ChartEndpoint; b: ChartEndpoint } {
  const a = { label: "Port A", reference: perf?.portA?.subId ?? null };
  const nonPortName = perf?.endpointB?.subscriptionId || perf?.endpointB?.name || null;
  if (perf?.endpointBType === "sdwan_hub") return { a, b: { label: "SD-WAN Hub", reference: nonPortName } };
  if (perf?.endpointBType === "fwaas") return { a, b: { label: "FWAAS", reference: nonPortName } };
  return { a, b: { label: "Port B", reference: perf?.portB?.subId ?? null } };
}

// Keeps the series whose legend toggle is on, paired as [shown, series].
function onlyShown(entries: [boolean, PeakSeries][]): PeakSeries[] {
  return entries.filter(([shown]) => shown).map(([, series]) => series);
}

type Props = {
  service: BandwidthService;       // "port" | "cloud" | "internet"
  id: string;                      // portId or connectionId
  tenantId: number;
  portType?: PortType;
  destination?: string;            // Optional: for latency filtering (Internet service)
  // Contracted capacity from the parent's detail (the same value as its Capacity
  // row); null hides the utilization percentage but not the peaks.
  capacityMbps?: number | null;
};

export default function BandwidthChartUniversal({ service, id, tenantId, portType, destination, capacityMbps = null }: Props) {
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const adapter = SERVICE_ADAPTERS[service];
  const slice = useAppSelector(adapter.select);

  const timeRange = slice.timeRange as Duration | null;
  const interval  = slice.interval  as Duration | null;
  const performanceData = slice.performanceData as PortPerformanceData | null;
  const performanceLoading = slice.performanceLoading;
  const performanceError   = slice.performanceError;
  
  // Custom date range state
  const useCustomDateRange = slice.useCustomDateRange ?? false;
  const customStartDate = slice.customStartDate;
  const customEndDate = slice.customEndDate;
  const maxPoints = slice.maxPoints ?? TARGET_POINTS;

  // The range also lives in the detail URL, so a reload or a pasted link restores it
  // (CTX-773). Redux stays the live value; the URL seeds it once per id and mirrors it after.
  const [searchParams, setSearchParams] = useSearchParams();

  // DateTime Picker state - sync with Redux
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: customStartDate ? new Date(customStartDate) : null,
    end: customEndDate ? new Date(customEndDate) : null,
  });
  
  // Sync local state with Redux
  useEffect(() => {
    if (customStartDate && customEndDate) {
      setDateRange({
        start: new Date(customStartDate),
        end: new Date(customEndDate),
      });
    } else if (!useCustomDateRange) {
      setDateRange({ start: null, end: null });
    }
  }, [customStartDate, customEndDate, useCustomDateRange]);
  
  // Date range validation
  const dateRangeValidation = useMemo(() => {
    if (!useCustomDateRange) return { isValid: true };
    // dateOnly=true porque el date picker usa dateOnly={true}
    // TEMPORAL: Pasar portType para aplicar regla de 8 días para puertos virtuales
    return validateDateRange(dateRange.start, dateRange.end, true, portType);
  }, [useCustomDateRange, dateRange.start, dateRange.end, portType]);
  
  // Compute range seconds for custom mode
  const rangeSeconds = useMemo(() => {
    if (!useCustomDateRange || !dateRange.start || !dateRange.end) return 0;
    return computeRangeSeconds(dateRange.start, dateRange.end);
  }, [useCustomDateRange, dateRange.start, dateRange.end]);

  // shared controls based on portType rules (virtual: 30m..7d; intervals >=5m, etc.)
  const tc = useMemo(() => buildControls(portType), [portType]);
  type TRKey = (typeof tc.order)[number];

  const CHANGE_INTERVALS_MAP = tc.map;
  const TR_OPTIONS = useMemo(() => getTimeRangeOptions(tc.presets), [tc.presets]);

  // What a view opens on when its URL asks for nothing: the smallest range and interval.
  // It is also what the URL leaves out, so a plain detail URL stays plain.
  const defaultRange = useMemo<ChartRange>(() => {
    const tr = tc.minTimeRange as TRKey;
    const opts = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    return { kind: "preset", timeRange: tr as Duration, interval: (opts[0]?.value as Duration | undefined) ?? null };
  }, [tc.minTimeRange, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

  // The URL's range if this chart can show it, else null. A preset keeps its interval only
  // when it is one of the range's options, and snaps to the smallest otherwise.
  const resolveRequestedRange = useCallback(
    (requested: ChartRange | null): ChartRange | null => {
      if (requested?.kind === "custom") {
        const valid = validateDateRange(new Date(requested.start), new Date(requested.end), true, portType).isValid;
        return valid ? requested : null;
      }
      if (requested?.kind !== "preset" || !(requested.timeRange in CHANGE_INTERVALS_MAP)) return null;
      const opts = getIntervalOptions(portType, requested.timeRange as TRKey, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
      const keepsInterval = opts.some((o) => o.value === requested.interval);
      return {
        kind: "preset",
        timeRange: requested.timeRange,
        interval: keepsInterval ? requested.interval : ((opts[0]?.value as Duration | undefined) ?? null),
      };
    },
    [portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]
  );

  // Mode selector options
  const MODE_OPTIONS = [
    { value: "preset", label: "Preset Time Range" },
    { value: "custom", label: "Custom Date Range" },
  ];

  // ===== Custom mode interval options =====
  const customIntervalOptions = useMemo<CustomIntervalOption[]>(() => {
    if (!useCustomDateRange || rangeSeconds <= 0) return [];
    return getIntervalOptionsForRange(rangeSeconds, portType, maxPoints);
  }, [useCustomDateRange, rangeSeconds, portType, maxPoints]);

  // ===== validity & readiness =====
  const configReady = useMemo(() => {
    if (useCustomDateRange) {
      // Custom mode validation
      if (!dateRangeValidation.isValid) return false;
      if (!dateRange.start || !dateRange.end) return false;
      if (!interval) return false;
      
      // Validate interval is safe
      const safety = isIntervalSafe(rangeSeconds, interval as ExtendedIntervalLiteral, portType);
      if (!safety.safe) return false;
      
      // Additional validation: check if interval is suboptimal
      // Block fetch if points are way above target (give auto-snap time to work)
      if (safety.points > TARGET_POINTS * 2) {
        // Check if current interval is in recommended options
        const recommendedOptions = getIntervalOptionsForRange(rangeSeconds, portType, maxPoints);
        const isInRecommendedOptions = recommendedOptions.some(opt => opt.value === interval);
        if (!isInRecommendedOptions) {
          // Interval is not optimal, wait for auto-snap
          return false;
        }
      }
      
      return true;
    } else {
      // Preset mode validation
      if (!timeRange || !interval) return false;
      const tr = timeRange as TRKey;
      if (!(tr in CHANGE_INTERVALS_MAP)) return false;
      const options = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
      const allowed = options.map(o => o.value);

      const intervalOk = allowed.includes(interval as IntervalLiteral);
      const intervalSeconds = toSeconds(interval);
      const timeRangeSeconds = toSeconds(tr);
      const backendOk = intervalSeconds <= timeRangeSeconds;
      
      // Validar que timeRange sea divisible por interval
      const divisible = timeRangeSeconds % intervalSeconds === 0;
      return intervalOk && backendOk && divisible;
    }
  }, [
    useCustomDateRange,
    dateRangeValidation.isValid,
    dateRange.start,
    dateRange.end,
    interval,
    rangeSeconds,
    portType,
    maxPoints,
    timeRange,
    CHANGE_INTERVALS_MAP,
    tc.minInterval,
    tc.maxInterval,
  ]);

  // ===== Initialize interval when entering custom mode =====
  const customModeInitializedRef = useRef<boolean>(false);
  
  // ===== Auto-snap interval when date range changes (custom mode) =====
  const prevRangeSecondsRef = useRef<number>(0);

  // ===== Reset custom mode when id or service changes =====
  const prevIdRef = useRef<string | null>(null);
  const prevServiceRef = useRef<BandwidthService | null>(null);
  
  useEffect(() => {
    const idChanged = prevIdRef.current !== null && prevIdRef.current !== id;
    const serviceChanged = prevServiceRef.current !== null && prevServiceRef.current !== service;
    
    if (idChanged || serviceChanged) {
      // Reset custom date range mode to default (preset mode)
      dispatch(adapter.setUseCustomDateRange(false));
      dispatch(adapter.clearCustomDateRange());
      dispatch(adapter.setMaxPoints(TARGET_POINTS));
      
      // Reset local state
      setDateRange({ start: null, end: null });
      
      // Reset refs related to custom mode
      customModeInitializedRef.current = false;
      prevRangeSecondsRef.current = 0;
    }
    
    prevIdRef.current = id;
    prevServiceRef.current = service;
  }, [id, service, dispatch, adapter]);

  // ===== Cleanup on unmount: Reset to Preset Time Range =====
  useEffect(() => {
    // Cleanup function: se ejecuta cuando el componente se desmonta
    return () => {
      // Reset Redux state to default (Preset Time Range)
      dispatch(adapter.setUseCustomDateRange(false));
      dispatch(adapter.clearCustomDateRange());
      dispatch(adapter.setMaxPoints(TARGET_POINTS));
    };
  }, [dispatch, adapter]); //Dispatch and adapter as dependencies

  // ===== seed ONCE per id: the URL's range, else the defaults =====
  const defaultsAppliedRef = useRef<string | null>(null);
  useEffect(() => { defaultsAppliedRef.current = null; }, [id]);

  // The range dispatched by the seed, until Redux reflects it. While it is pending, the
  // effects that react to the range (snap, coerce, fetch, URL mirror) hold still: on the
  // first commit they would otherwise act on whatever the slice held from the previous
  // view, and the snap would clobber a restored interval with the smallest one.
  const seedRef = useRef<ChartRange | null>(null);
  const prevTRRef = useRef<TRKey | null>(null);

  useEffect(() => {
    if (defaultsAppliedRef.current === id) return;
    
    // Only apply defaults if not in custom mode (custom mode will be reset above)
    if (useCustomDateRange) {
      // Wait for reset to complete, will retry on next render
      return;
    }

    const seed = resolveRequestedRange(readChartRange(searchParams)) ?? defaultRange;
    if (seed.kind === "custom") {
      dispatch(adapter.setUseCustomDateRange(true));
      dispatch(adapter.setCustomDateRange({ start: seed.start, end: seed.end }));
    } else if (timeRange !== seed.timeRange || interval !== seed.interval) {
      // If the slice has setTimeControls, use it to avoid intermediate invalid pairs
      if (adapter.setTimeControls && seed.interval) {
        dispatch(adapter.setTimeControls({ timeRange: seed.timeRange, interval: seed.interval }));
      } else {
        dispatch(adapter.setTimeRange(seed.timeRange));
        if (seed.interval) dispatch(adapter.setInterval(seed.interval));
      }
    }

    seedRef.current = seed;
    defaultsAppliedRef.current = id;
  }, [id, timeRange, interval, useCustomDateRange, searchParams, resolveRequestedRange, defaultRange, dispatch, adapter]);

  // Release the seed once the slice holds it. No deps: it has to see every render.
  useEffect(() => {
    const seed = seedRef.current;
    if (!seed) return;
    const settled = seed.kind === "custom"
      ? useCustomDateRange && customStartDate === seed.start && customEndDate === seed.end
      : !useCustomDateRange && timeRange === seed.timeRange && interval === seed.interval;
    if (!settled) return;
    seedRef.current = null;
    // The seeded interval is the one to keep: tell the snap below this range is not new.
    if (seed.kind === "preset") prevTRRef.current = seed.timeRange as TRKey;
  });

  // ===== coerce if TR not in this preset set (portType switch) =====
  useEffect(() => {
    if (seedRef.current) return;
    const tr = timeRange as TRKey | null;
    if (!tr || tr in CHANGE_INTERVALS_MAP) return;
    const fallback = tc.minTimeRange as TRKey;
    const opts = getIntervalOptions(portType, fallback, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const smallest = opts[0]?.value as IntervalLiteral | undefined;
    if (adapter.setTimeControls && smallest) {
      dispatch(adapter.setTimeControls({ timeRange: fallback, interval: smallest }));
    } else {
      dispatch(adapter.setTimeRange(fallback));
      if (smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [timeRange, CHANGE_INTERVALS_MAP, tc.minTimeRange, portType, dispatch, adapter, tc.minInterval, tc.maxInterval]);

  // ===== snap interval on TR change =====
  useEffect(() => {
    if (seedRef.current) return;
    if (useCustomDateRange) return; // Skip in custom mode
    const tr = timeRange as TRKey | null;
    if (!tr || !(tr in CHANGE_INTERVALS_MAP)) return;
    if (prevTRRef.current !== tr) {
      prevTRRef.current = tr;
      const opts = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
      const smallest = opts[0]?.value as IntervalLiteral | undefined;
      // only snap on TR change
      if (smallest && interval !== smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [timeRange, interval, portType, useCustomDateRange, CHANGE_INTERVALS_MAP, dispatch, adapter, tc.minInterval, tc.maxInterval]);

  // ===== Initialize interval when entering custom mode =====
  useEffect(() => {
    if (!useCustomDateRange) {
      customModeInitializedRef.current = false;
      return;
    }
    
    if (customModeInitializedRef.current) return;
    
    if (rangeSeconds > 0 && dateRange.start && dateRange.end) {
      const recommended = pickIntervalForRange(rangeSeconds, portType, maxPoints);
      if (recommended) {
        dispatch(adapter.setInterval(recommended as any));
        customModeInitializedRef.current = true;
      }
    }
  }, [useCustomDateRange, rangeSeconds, dateRange.start, dateRange.end, portType, maxPoints, dispatch, adapter, interval]);

  // ===== Auto-snap interval when date range changes (custom mode) =====
  useEffect(() => {
    if (!useCustomDateRange || rangeSeconds <= 0) {
      prevRangeSecondsRef.current = 0;
      return;
    }
    
    const prevRange = prevRangeSecondsRef.current;
    const rangeChanged = Math.abs(rangeSeconds - prevRange) >= 60;
    
    // Only proceed if range changed significantly (avoid infinite loops)
    if (!rangeChanged && prevRange > 0) return;
    
    // Calculate if range change is significant (> 50% of previous range)
    const rangeChangePercent = prevRange > 0 
      ? Math.abs(rangeSeconds - prevRange) / prevRange 
      : 1; // First time, consider it significant
    const isSignificantChange = rangeChangePercent > 0.5;
    
    prevRangeSecondsRef.current = rangeSeconds;
    
    // Check if current interval needs to be updated
    if (interval) {
      const safety = isIntervalSafe(rangeSeconds, interval as ExtendedIntervalLiteral, portType);
      const currentPoints = safety.points;
      
      // Get recommended interval and its options
      const recommended = pickIntervalForRange(rangeSeconds, portType, maxPoints);
      const recommendedOptions = getIntervalOptionsForRange(rangeSeconds, portType, maxPoints);
      const isInRecommendedOptions = recommendedOptions.some(opt => opt.value === interval);
      
      // Determine if we should snap:
      // 1. Interval is unsafe (out of bounds or too many points)
      // 2. Points are significantly above target (> 1.5x TARGET_POINTS = 270)
      // 3. Range changed significantly (> 50%)
      // 4. Current interval is not in recommended options
      const shouldSnap = 
        !safety.safe || 
        currentPoints > HARD_MAX_POINTS ||
        currentPoints > TARGET_POINTS * 1.5 ||
        isSignificantChange ||
        !isInRecommendedOptions;
      
      if (!shouldSnap && recommended === interval) {
        return; // Current interval is optimal
      }
      
      // Auto-snap to recommended interval
      if (recommended && recommended !== interval) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[BandwidthChart] Auto-snapping interval:', {
            from: interval,
            to: recommended,
            reason: !safety.safe ? 'unsafe' :
                   currentPoints > HARD_MAX_POINTS ? 'too_many_points' :
                   currentPoints > TARGET_POINTS * 1.5 ? 'suboptimal_points' :
                   isSignificantChange ? 'significant_range_change' :
                   !isInRecommendedOptions ? 'not_in_recommended' : 'other',
            currentPoints,
            targetPoints: TARGET_POINTS,
            rangeChangePercent: (rangeChangePercent * 100).toFixed(1) + '%',
          });
        }
        dispatch(adapter.setInterval(recommended as any));
        return; // Exit early after snap
      }
    } else {
      // No interval set, use recommended
      const recommended = pickIntervalForRange(rangeSeconds, portType, maxPoints);
      if (recommended) {
        dispatch(adapter.setInterval(recommended as any));
      }
    }
  }, [useCustomDateRange, rangeSeconds, interval, portType, maxPoints, dispatch, adapter]);

  // The answer for exactly this chart and range, cached across view switches: coming back
  // paints the last one at once and the fetch below refreshes it (project 13D).
  const perfKey = useMemo(() => {
    if (!interval) return null;
    const range =
      useCustomDateRange && customStartDate && customEndDate
        ? `from=${customStartDate}&to=${customEndDate}&maxPoints=${maxPoints}`
        : `range=${timeRange ?? ""}`;
    return `perf/${service}/${id}?tenantId=${tenantId}&${range}&interval=${interval}&dest=${destination ?? ""}`;
  }, [service, id, tenantId, useCustomDateRange, customStartDate, customEndDate, maxPoints, timeRange, interval, destination]);
  const hasCachedPerformance = readCachedFetch(perfKey) !== undefined;

  // ===== FETCH ONLY WHEN READY =====
  useEffect(() => {
    if (seedRef.current) return;
    if (!configReady) return;
    if (!interval) return;

    const cached = readCachedFetch(perfKey);
    if (cached !== undefined) dispatch(adapter.primePerformanceData(cached));
    const remember = (action: { payload?: unknown; meta?: { requestStatus?: string } }) => {
      if (action?.meta?.requestStatus === "fulfilled") writeCachedFetch(perfKey, action.payload);
    };

    if (useCustomDateRange && customStartDate && customEndDate) {
      // Custom mode: use start/end dates from Redux (already in ISO UTC format)
      const startISO = customStartDate;
      const endISO = customEndDate;
      
      // Instrumentación en dev
      if (process.env.NODE_ENV === 'development') {
        const points = calculatePoints(rangeSeconds, interval as ExtendedIntervalLiteral);
        console.debug('[BandwidthChart] Custom mode fetch:', {
          start: startISO,
          end: endISO,
          interval,
          maxPoints,
          estimatedPoints: points,
          rangeSeconds,
        });
      }
      
      dispatch(adapter.fetchThunk({
        api, tenantId,
        interval,
        startDate: startISO,
        endDate: endISO,
        maxPoints,
        id,
        destination,
      })).then(remember);
    } else if (timeRange) {
      // Preset mode: use timeRange
      if (process.env.NODE_ENV === 'development') {
        console.debug('[BandwidthChart] Preset mode fetch:', {
          timeRange,
          interval,
        });
      }
      
      dispatch(adapter.fetchThunk({
        api, tenantId,
        timeRange, interval,
        id,
        destination,
      })).then(remember);
    }
  }, [
    configReady,
    useCustomDateRange,
    dateRange.start,
    dateRange.end,
    timeRange,
    interval,
    rangeSeconds,
    maxPoints,
    api,
    tenantId,
    id,
    destination,
    dispatch,
    adapter,
    perfKey,
  ]);

  // ===== mirror the range into the URL =====
  // Replace, not push: Back leaves the detail instead of stepping through every range tried.
  useEffect(() => {
    if (defaultsAppliedRef.current !== id || seedRef.current) return;
    let current: ChartRange | null = null;
    if (useCustomDateRange) {
      // A custom range with a date still missing is not a range yet.
      if (customStartDate && customEndDate) current = { kind: "custom", start: customStartDate, end: customEndDate };
    } else if (timeRange) {
      current = { kind: "preset", timeRange, interval };
    }
    const wanted = sameChartRange(current, defaultRange) ? null : current;
    if (sameChartRange(readChartRange(searchParams), wanted)) return;
    setSearchParams(writeChartRange(searchParams, wanted), { replace: true });
  }, [id, useCustomDateRange, customStartDate, customEndDate, timeRange, interval, defaultRange, searchParams, setSearchParams]);

  // ===== dropdown options =====
  const intervalOptions = useMemo<IntervalOption[]>(() => {
    if (useCustomDateRange) {
      // Return custom interval options (may include ExtendedIntervalLiteral)
      return customIntervalOptions.map(opt => ({
        value: opt.value as any, // ExtendedIntervalLiteral includes IntervalLiteral
        label: opt.label,
      }));
    }
    // Preset mode options
    const tr = timeRange as TRKey;
    if (!tr || !(tr in CHANGE_INTERVALS_MAP)) return [];
    return getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
  }, [useCustomDateRange, customIntervalOptions, timeRange, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

// Time range handler (universal)
const handleTimeRangeSelect = useCallback((choice: string | number) => {
    const opt = typeof choice === "number"
      ? TR_OPTIONS[choice]
      : TR_OPTIONS.find(o => o.label === choice || o.value === choice);
    if (!opt) return;
    const tr = opt.value as TRKey;
    // compute first allowed interval for that TR
    const opts = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const smallest = opts[0]?.value as IntervalLiteral | undefined;
    // atomic update if available
    if (adapter.setTimeControls && smallest) {
      dispatch(adapter.setTimeControls({ timeRange: tr, interval: smallest }));
    } else {
      dispatch(adapter.setTimeRange(tr));
      if (smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [TR_OPTIONS, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval, adapter, dispatch]);
  
  
  // Interval handler (universal)
  const handleIntervalSelect = useCallback((choice: string | number) => {
    const opt = typeof choice === "number"
      ? intervalOptions[choice]
      : intervalOptions.find(o => o.label === choice || o.value === choice);
    if (!opt) return;
    dispatch(adapter.setInterval(opt.value as IntervalLiteral));
  }, [intervalOptions, adapter, dispatch]);

  // Mode selector handler
  const handleModeSelect = useCallback((choice: string | number) => {
    const opt = typeof choice === "number"
      ? MODE_OPTIONS[choice]
      : MODE_OPTIONS.find(o => o.label === choice || o.value === choice);
    if (!opt) return;
    
    const isCustom = opt.value === "custom";
    dispatch(adapter.setUseCustomDateRange(isCustom));
    
    if (!isCustom) {
      // Switching back to preset mode - clear custom dates and restore defaults
      dispatch(adapter.clearCustomDateRange());
      const desiredTR = tc.minTimeRange as TRKey;
      const opts = getIntervalOptions(portType, desiredTR, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
      const smallest = opts[0]?.value as IntervalLiteral | undefined;
      if (smallest && adapter.setTimeControls) {
        dispatch(adapter.setTimeControls({ timeRange: desiredTR, interval: smallest }));
      }
    }
  }, [dispatch, adapter, tc.minTimeRange, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

  // Calculate min and max dates for the date picker
  const minDate = useMemo(() => {
    const now = new Date();
    const maxPast = new Date(now);
    // TEMPORAL: Para puertos virtuales, usar 8 días en lugar de 90
    const maxRangeDays = portType === "virtual" ? VIRTUAL_PORT_MAX_RANGE_DAYS : MAX_RANGE_DAYS;
    maxPast.setDate(maxPast.getDate() - maxRangeDays);
    maxPast.setHours(0, 0, 0, 0);
    return maxPast;
  }, [portType]);

  const maxDate = useMemo(() => {
    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    return endOfToday;
  }, []);

  // Date range change handler
  const handleDateRangeChange = useCallback((value: { start: Date | null; end: Date | null }) => {
    // Actualizar estado local siempre (para que el usuario vea su selección)
    setDateRange(value);
    
    // Solo normalizar y guardar en Redux cuando ambas fechas están seleccionadas
    if (value.start && value.end) {
      // Normalizar y validar el rango usando la función única (single source of truth)
      const normalized = normalizeDateRange(value.start, value.end);
      if (normalized) {
        // Actualizar estado local con fechas normalizadas
        setDateRange(normalized);
        
        // Convertir a ISO UTC usando dateRangeToISOUTC (America/Mexico_City → ISO UTC)
        const isoDates = dateRangeToISOUTC(normalized.start, normalized.end);
        
        // Guardar en Redux
        dispatch(adapter.setCustomDateRange({
          start: isoDates.startISO,
          end: isoDates.endISO,
        }));
      } else {
        // Si el rango es inválido después de normalizar, no guardar en Redux
        // pero mantener el estado local para que el usuario vea su selección
      }
    } else {
      // Si solo hay startDate o endDate, no guardar en Redux todavía
      // (esperamos a que ambas fechas estén seleccionadas)
    }
  }, [dispatch, adapter]);

  // ===== chart UI =====
  const isDatacenter = service === "datacenter";
  const [showInbound, setShowInbound] = useState(true);
  const [showOutbound, setShowOutbound] = useState(true);
  // For datacenter, we need separate states for each port's inbound/outbound
  const [showPortAInbound, setShowPortAInbound] = useState(true);
  const [showPortAOutbound, setShowPortAOutbound] = useState(true);
  const [showPortBInbound, setShowPortBInbound] = useState(true);
  const [showPortBOutbound, setShowPortBOutbound] = useState(true);

  // Reset visibility states when service or id changes
  useEffect(() => {
    setShowInbound(true);
    setShowOutbound(true);
    setShowPortAInbound(true);
    setShowPortAOutbound(true);
    setShowPortBInbound(true);
    setShowPortBOutbound(true);
  }, [service, id]);

  // Handlers for toggling visibility via clickable labels
  const handleToggleInbound = () => setShowInbound(prev => !prev);
  const handleToggleOutbound = () => setShowOutbound(prev => !prev);
  const handleTogglePortAInbound = () => setShowPortAInbound(prev => !prev);
  const handleTogglePortAOutbound = () => setShowPortAOutbound(prev => !prev);
  const handleTogglePortBInbound = () => setShowPortBInbound(prev => !prev);
  const handleTogglePortBOutbound = () => setShowPortBOutbound(prev => !prev);


  // Normalize the payload for display: convert to a readable unit
  // (Kbps/Mbps/Gbps) and round to 2 decimals. The backend already does this,
  // but this acts as an idempotent fallback so the chart stays readable even
  // when the API returns raw sub-Mbps values still labelled as Mbps.
  const normalized = useMemo<{ data: PortPerformanceData | null; unit: string }>(() => {
    if (!performanceData) {
      return { data: null, unit: "Mbps" };
    }

    if (isDatacenter && performanceData.portA && performanceData.portB) {
      const result = normalizeDciBandwidthForDisplay(
        performanceData.portA.bandwidthUtilization,
        performanceData.portB.bandwidthUtilization,
        performanceData.bandwidthUnit
      );
      return {
        unit: result.unit,
        data: {
          ...performanceData,
          bandwidthUnit: result.unit,
          portA: {
            ...performanceData.portA,
            bandwidthUtilization: {
              ...performanceData.portA.bandwidthUtilization,
              inbound: result.portA.inbound,
              outbound: result.portA.outbound,
              unit: result.unit,
            },
          },
          portB: {
            ...performanceData.portB,
            bandwidthUtilization: {
              ...performanceData.portB.bandwidthUtilization,
              inbound: result.portB.inbound,
              outbound: result.portB.outbound,
              unit: result.unit,
            },
          },
        },
      };
    }

    if (performanceData.bandwidthUtilization) {
      const result = normalizePortBandwidthForDisplay(performanceData.bandwidthUtilization);
      return {
        unit: result.unit,
        data: {
          ...performanceData,
          bandwidthUtilization: {
            ...performanceData.bandwidthUtilization,
            inbound: result.inbound,
            outbound: result.outbound,
            unit: result.unit,
          },
        },
      };
    }

    return { data: performanceData, unit: "Mbps" };
  }, [performanceData, isDatacenter]);

  const chartData = useMemo(() => processData(normalized.data, isDatacenter), [normalized.data, isDatacenter]);
  const bandwidthUnit = normalized.unit;

  const hasData = chartData.length > 0;
  const hasError = !!performanceError || performanceData === null;
  
  const endpoints = useMemo(() => dciEndpoints(performanceData), [performanceData]);

  // Peaks read the raw series in its declared unit — the display copy above is
  // rounded, and DCI's also turns gaps into zeros. One pair per port on DCI:
  // adding A and B would count the same traffic twice. A series hidden from the
  // chart through its legend toggle is left out here too (CTX-779).
  const peakSeries = useMemo<PeakSeries[]>(() => {
    if (isDatacenter) {
      const portA = performanceData?.portA?.bandwidthUtilization;
      const portB = performanceData?.portB?.bandwidthUtilization;
      const unit = performanceData?.bandwidthUnit ?? portA?.unit ?? portB?.unit ?? "Mbps";
      return onlyShown([
        [showPortAInbound, { key: "inboundA", direction: "inbound", endpoint: endpoints.a, peakMbps: peakMbps(portA?.inbound, unit) }],
        [showPortAOutbound, { key: "outboundA", direction: "outbound", endpoint: endpoints.a, peakMbps: peakMbps(portA?.outbound, unit) }],
        [showPortBInbound, { key: "inboundB", direction: "inbound", endpoint: endpoints.b, peakMbps: peakMbps(portB?.inbound, unit) }],
        [showPortBOutbound, { key: "outboundB", direction: "outbound", endpoint: endpoints.b, peakMbps: peakMbps(portB?.outbound, unit) }],
      ]);
    }
    const bandwidth = performanceData?.bandwidthUtilization;
    const unit = bandwidth?.unit ?? "Mbps";
    return onlyShown([
      [showInbound, { key: "inbound", direction: "inbound", peakMbps: peakMbps(bandwidth?.inbound, unit) }],
      [showOutbound, { key: "outbound", direction: "outbound", peakMbps: peakMbps(bandwidth?.outbound, unit) }],
    ]);
  }, [
    performanceData, isDatacenter, endpoints,
    showInbound, showOutbound, showPortAInbound, showPortAOutbound, showPortBInbound, showPortBOutbound,
  ]);

  return (
    <div className="py-5 space-y-10">
      <GenericCard
        headerContent={
        <div className="flex flex-col xl:flex-row md:items-center md:justify-between gap-4 xl:gap-0 w-full">
          <h3 className="text-body-medium font-bold w-full xl:w-auto">Bandwidth Utilization</h3>
          <div className="flex flex-col md:flex-col xl:flex-row content-center gap-4 md:gap-4 xl:gap-0 w-full md:w-fit xl:w-auto md:self-start">
          {/* <div className="flex flex-col md:flex-col xl:flex-row content-center gap-4 md:gap-4 xl:gap-0 w-auto md:w-fit xl:w-auto md:self-start"> */}
            {/* Dropdowns */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-0 w-full md:w-auto">
              {/* Mode Selector - First: rounded left only, keep right border */}
              <div className="flex-col flex-1 w-full">
                <div className="h-7 flex items-center justify-between">
                  {/* <p className="text-sm">Mode</p>
                  <div className="w-4" /> */}
                </div>
                <div className="[&_button]:rounded-sm md:[&_button]:rounded-l-sm md:[&_button]:rounded-r-none [&_button]:w-full">
                  <GenericDropdown
                    items={MODE_OPTIONS.map(o => o.label)}
                    value={MODE_OPTIONS.find(o => o.value === (useCustomDateRange ? "custom" : "preset"))?.label || "Time Range"}
                    onSelect={handleModeSelect}
                    defaultLabel="Time Range"
                    widthClass="w-full"
                    size="sm"
                    borderRadiusClass="rounded-sm md:rounded-l-sm md:rounded-r-none"
                    maxWidthClass="max-w-none md:max-w-[200px]"
                  />
                </div>
              </div>
              {useCustomDateRange ? (
                /* Custom Date Range Mode */
                <>
                  {/* Date Range - Middle: no rounded corners, keep both borders */}
                  <div className="flex-col flex-1 w-full md:min-w-[200px]">
                    <div className="h-7 flex items-center justify-between">
                      <p className="text-sm">Date Range</p>
                      <div className="w-4" />
                    </div>
                    <div className="[&_input]:rounded-sm md:[&_input]:rounded-none [&_input]:w-full">
                      <GenericDateTimeRangePicker
                        label=""
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        displayFormat="dd/MM/yyyy"
                        disabled={!useCustomDateRange}
                        dateOnly={true}
                        minDate={minDate}
                        maxDate={maxDate}
                        borderRadiusClass="rounded-sm md:rounded-none"
                      />
                    </div>
                  </div>
                  {/* Interval - Last: rounded right only, keep left border */}
                  <div className="flex flex-col flex-1 w-full">
                    <div className="h-7 flex items-center justify-between">
                      <p className="display text-sm content-center">Change Interval</p>
                      <GenericTooltip text="Interval is auto-calculated based on date range. Shorter intervals show more detail." width="20rem" horizontalAlign="right" />
                    </div>
                    <div className="[&_button]:rounded-sm md:[&_button]:rounded-r-sm md:[&_button]:rounded-l-none [&_button]:w-full">
                      <GenericDropdown
                        items={intervalOptions.map(o => o.label)}
                        value={intervalOptions.find(o => o.value === interval)?.label ?? "Select Interval"}
                        onSelect={handleIntervalSelect}
                        defaultLabel="Select Interval"
                        widthClass="w-full"
                        size="sm"
                        borderRadiusClass="rounded-sm md:rounded-r-sm md:rounded-l-none"
                        maxWidthClass="max-w-none md:max-w-[200px]"
                      />
                    </div>

                  </div>
                </>
              ) : (
                /* Preset Time Range Mode */
                <>
                  {/* Time range - Middle: no rounded corners, keep both borders */}
                  <div className="flex-col flex-1 w-full">
                    <div className="h-7 flex items-center justify-between">
                      <p className="text-sm">Time Range</p>
                      <div className="w-4" />
                    </div>
                    <div className="[&_button]:rounded-sm md:[&_button]:rounded-none [&_button]:w-full">
                      <GenericDropdown
                        items={TR_OPTIONS.map(o => o.label)}
                        value={TR_OPTIONS.find(o => o.value === timeRange)?.label || TR_OPTIONS[0]?.label || "Select Time Range"}
                        onSelect={handleTimeRangeSelect}
                        defaultLabel="Select Time Range"
                        widthClass="w-full"
                        size="sm"
                        borderRadiusClass="rounded-sm md:rounded-none"
                        maxWidthClass="max-w-none md:max-w-[200px]"
                      />
                    </div>
                  </div>
                  {/* Interval - Last: rounded right only, keep left border */}
                  <div className="flex flex-col flex-1 w-full">
                    <div className="h-7 flex items-center justify-between">
                      <p className="display text-sm content-center">Change Interval</p>
                      <GenericTooltip text="Shorter intervals show more detail; longer ones smooth trends." width="20rem" horizontalAlign="right" />
                    </div>
                    <div className="[&_button]:rounded-sm md:[&_button]:rounded-r-sm md:[&_button]:rounded-l-none [&_button]:w-full">
                      <GenericDropdown
                        items={intervalOptions.map(o => o.label)}
                        value={intervalOptions.find(o => o.value === interval)?.label ?? "Select Interval"}
                        onSelect={handleIntervalSelect}
                        defaultLabel="Select Interval"
                        widthClass="w-full"
                        size="sm"
                        borderRadiusClass="rounded-sm md:rounded-r-sm md:rounded-l-none"
                        maxWidthClass="max-w-none md:max-w-[200px]"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        }
        rightContent={null}
        isCollapsible={false}
        bodyContent={
          <>
            {(!performanceLoading || hasCachedPerformance) && (
              <BandwidthPeaks series={peakSeries} capacityMbps={capacityMbps} interval={interval} />
            )}
            <div className="relative h-[300px]">
              {(!performanceLoading || hasCachedPerformance) && (hasError || !hasData) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <IconInfo className="w-8 h-8 text-yellow-500" />
                  <p className="mt-2 text-sm text-semantic-text-secondary">Performance data is unavailable at the moment.</p>
                </div>
              )}
              <div className="relative w-90% mx-5">
                <ChartContainer
                  config={{
                    inbound: { label: "Inbound", color: "var(--color-primitive-brand-surf-300)" },
                    outbound: { label: "Outbound", color: "var(--color-primitive-brand-surf-700)" },
                  }}
                  className="h-[300px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 30, right: 50, left: 40, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="formattedTime" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12, fill: "#666" }} interval="preserveStartEnd">
                        <Label value="Time (Mexico City)" position="bottom" offset={20} style={{ textAnchor: "middle", fontSize: 12 }} />
                      </XAxis>
                      <YAxis tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12, fill: "#666" }} tickFormatter={(v) => `${v}`}>
                        <Label value="Mean Bandwidth Usage" angle={-90} position="insideLeft" offset={-23} style={{ textAnchor: "middle", fontSize: 12 }} />
                        <Label value={`(${bandwidthUnit})`} angle={-90} position="insideLeft" offset={-5} style={{ textAnchor: "middle", fontSize: 12 }} />
                      </YAxis>
                      <Tooltip formatter={(value: any, name: any) => [`${value} ${bandwidthUnit}`, name]} />
                      {isDatacenter ? (
                        <>
                          {showPortAInbound && (
                            <Line type="monotone" dataKey="inboundA" name={`${endpoints.a.label} Inbound`} stroke={DCI_SERIES_COLORS.inboundA} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                          )}
                          {showPortAOutbound && (
                            <Line type="monotone" dataKey="outboundA" name={`${endpoints.a.label} Outbound`} stroke={DCI_SERIES_COLORS.outboundA} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                          )}
                          {showPortBInbound && (
                            <Line type="monotone" dataKey="inboundB" name={`${endpoints.b.label} Inbound`} stroke={DCI_SERIES_COLORS.inboundB} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                          )}
                          {showPortBOutbound && (
                            <Line type="monotone" dataKey="outboundB" name={`${endpoints.b.label} Outbound`} stroke={DCI_SERIES_COLORS.outboundB} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                          )}
                        </>
                      ) : (
                        <>
                          {showInbound && (
                            <Line type="monotone" dataKey="inbound" name="Inbound" stroke="var(--color-inbound)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                          )}
                          {showOutbound && (
                            <Line type="monotone" dataKey="outbound" name="Outbound" stroke="var(--color-outbound)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                          )}
                        </>
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
                {/* A cached chart stays readable while it refreshes; only a first load blocks it. */}
                {performanceLoading && !hasCachedPerformance && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <span className="text-lg font-semibold text-gray-700">Loading...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end my-5 mr-10">
              <div className="flex flex-wrap items-center gap-4">
                {isDatacenter ? (
                  <>
                    {/* Port A Inbound */}
                    <div
                      onClick={handleTogglePortAInbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showPortAInbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DCI_SERIES_COLORS.inboundA }} />
                      <span className="text-sm">{`${endpoints.a.label} Inbound`}</span>
                    </div>
                    {/* Port A Outbound */}
                    <div
                      onClick={handleTogglePortAOutbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showPortAOutbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DCI_SERIES_COLORS.outboundA }} />
                      <span className="text-sm">{`${endpoints.a.label} Outbound`}</span>
                    </div>
                    {/* Port B Inbound */}
                    <div
                      onClick={handleTogglePortBInbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showPortBInbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DCI_SERIES_COLORS.inboundB }} />
                      <span className="text-sm">{`${endpoints.b.label} Inbound`}</span>
                    </div>
                    {/* Port B Outbound */}
                    <div
                      onClick={handleTogglePortBOutbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showPortBOutbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DCI_SERIES_COLORS.outboundB }} />
                      <span className="text-sm">{`${endpoints.b.label} Outbound`}</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Inbound */}
                    <div
                      onClick={handleToggleInbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showInbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-300)]" />
                      <span className="text-sm">Inbound</span>
                    </div>
                    {/* Outbound */}
                    <div
                      onClick={handleToggleOutbound}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        showOutbound
                          ? 'border border-[var(--color-semantic-border-brand-secondary)] bg-[var(--color-semantic-bg-primary)] text-[var(--color-semantic-text-primary)]'
                          : 'border border-transparent text-[var(--color-primitive-gray-400)]'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-700)]" />
                      <span className="text-sm">Outbound</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        }
      />
    </div>
  );
}
